import { useState, useEffect } from "react";
import DualToggleSwitch from "../../../shared/dual-toggle-switch";
import Time from "../../../shared/time";
import FilmaizeText from "../../../shared/filmanize-text";
import FilmanizeCheckbox from "../../../shared/filmanize-checkbox";
import Collapsible from "../../../shared/collapsible";
import Progress from "../../../shared/progress";
import Loader from "../../../shared/loader";

interface SmarterDaysProps {
    filmId: number;
    createSchedule: (constraints: any) => void;
    fetchSchedulingConstraints: (filmId: number) => void;
    creatingScheduleLoading: boolean;
    creatingScheduleProgress?: any;
    fetchingConstraints?: boolean;
    schedulingConstraints?: any;
    isAdvancedSchedulingOpen: boolean;
    setIsAdvancedSchedulingOpen: (isOpen: boolean) => void;
}

const SmarterDays = ({
    filmId,
    createSchedule,
    fetchSchedulingConstraints,
    creatingScheduleLoading,
    creatingScheduleProgress,
    fetchingConstraints,
    schedulingConstraints,
    isAdvancedSchedulingOpen,
    setIsAdvancedSchedulingOpen
}: SmarterDaysProps) => {

    const [optimizeOnBudget, setOptimizeOnBudget] = useState(false);
    const [dayLength, setDayLength] = useState({ hours: 12, minutes: 0, days: 0 });
    const [earliestCallTime, setEarliestCallTime] = useState({ hours: 8, minutes: 0, days: 0 });
    const [scriptPagesPerDay, setScriptPagesPerDay] = useState(3);
    const [allowMultipleLocationsPerDay, setAllowMultipleLocationsPerDay] = useState(false);
    const [maximumTravelTimeBetweenLocations, setMaximumTravelTimeBetweenLocations] = useState({ hours: 2, minutes: 0, days: 0 });
    const [targetShootingDays, setTargetShootingDays] = useState<any>();
    const [percentageOverDayLengthAllowed, setPercentageOverDayLengthAllowed] = useState<number>(20);
    const [lunchDuration, setLunchDuration] = useState({ hours: 1, minutes: 0, days: 0 });
    const [dinnerDuration, setDinnerDuration] = useState({ hours: 1, minutes: 0, days: 0 });
    const [lunchWindowFrom, setLunchWindowFrom] = useState({ hours: 12, minutes: 0, days: 0 });
    const [lunchWindowTo, setLunchWindowTo] = useState({ hours: 14, minutes: 0, days: 0 });
    const [dinnerWindowFrom, setDinnerWindowFrom] = useState({ hours: 18, minutes: 0, days: 0 });
    const [dinnerWindowTo, setDinnerWindowTo] = useState({ hours: 20, minutes: 0, days: 0 });
    const [sunsetTime, setSunsetTime] = useState({ hours: 18, minutes: 0, days: 0 });
    const [mealThreshold1, setMealThreshold1] = useState({ hours: 4, minutes: 0, days: 0 });
    const [mealThreshold2, setMealThreshold2] = useState({ hours: 10, minutes: 0, days: 0 });

    // Update all fields when schedulingConstraints are loaded from the database
    useEffect(() => {
        if (schedulingConstraints) {
            if (schedulingConstraints.optimizationMode !== undefined) setOptimizeOnBudget(schedulingConstraints.optimizationMode === 1);
            if (schedulingConstraints.dayLength) setDayLength(schedulingConstraints.dayLength);
            if (schedulingConstraints.earliestCallTime) setEarliestCallTime(schedulingConstraints.earliestCallTime);
            if (schedulingConstraints.scriptPagesPerDay !== undefined) setScriptPagesPerDay(schedulingConstraints.scriptPagesPerDay);
            if (schedulingConstraints.allowMultipleLocationsPerDay !== undefined) setAllowMultipleLocationsPerDay(schedulingConstraints.allowMultipleLocationsPerDay);
            if (schedulingConstraints.maximumTravelTimeBetweenLocations) setMaximumTravelTimeBetweenLocations(schedulingConstraints.maximumTravelTimeBetweenLocations);
            if (schedulingConstraints.targetShootingDays !== undefined) setTargetShootingDays(schedulingConstraints.targetShootingDays);
            if (schedulingConstraints.percentageOverDayLengthAllowed !== undefined) setPercentageOverDayLengthAllowed(schedulingConstraints.percentageOverDayLengthAllowed);
            if (schedulingConstraints.lunchDuration) setLunchDuration(schedulingConstraints.lunchDuration);
            if (schedulingConstraints.dinnerDuration) setDinnerDuration(schedulingConstraints.dinnerDuration);
            if (schedulingConstraints.lunchWindowFrom) setLunchWindowFrom(schedulingConstraints.lunchWindowFrom);
            if (schedulingConstraints.lunchWindowTo) setLunchWindowTo(schedulingConstraints.lunchWindowTo);
            if (schedulingConstraints.dinnerWindowFrom) setDinnerWindowFrom(schedulingConstraints.dinnerWindowFrom);
            if (schedulingConstraints.dinnerWindowTo) setDinnerWindowTo(schedulingConstraints.dinnerWindowTo);
            if (schedulingConstraints.sunsetTime) setSunsetTime(schedulingConstraints.sunsetTime);
            if (schedulingConstraints.mealThreshold1) setMealThreshold1(schedulingConstraints.mealThreshold1);
            if (schedulingConstraints.mealThreshold2) setMealThreshold2(schedulingConstraints.mealThreshold2);
        }
    }, [schedulingConstraints]);


    const onCreateSchedule = () => {
        const constraints = {
            optimizationMode: optimizeOnBudget ? 1 : 0,
            dayLength,
            earliestCallTime,
            scriptPagesPerDay,
            allowMultipleLocationsPerDay,
            maximumTravelTimeBetweenLocations,
            targetShootingDays,
            percentageOverDayLengthAllowed,
            lunchDuration,
            dinnerDuration,
            lunchWindowFrom,
            lunchWindowTo,
            dinnerWindowFrom,
            dinnerWindowTo,
            sunsetTime,
            mealThreshold1,
            mealThreshold2
        };
        createSchedule(constraints);
    }

    const handleToggle = (isOpen: boolean) => {
        setIsAdvancedSchedulingOpen(isOpen);
    };

    return <div className="mt-1" style={{ maxWidth: '650px' }}>
        <Collapsible title="Advanced Scheduling" isOpen={isAdvancedSchedulingOpen} onToggle={handleToggle}>
            <div style={{ position: 'relative', opacity: fetchingConstraints ? 0.5 : 1, pointerEvents: fetchingConstraints ? 'none' : 'auto', minHeight: '1168px' }}>
                {fetchingConstraints && (
                    <div>
                        <Loader />
                    </div>
                )}
                {!fetchingConstraints && <>
                    <div className="mb-2 mt-1">
                        <label className="label">Minimize</label>
                        <DualToggleSwitch
                            checked={optimizeOnBudget}
                            onChange={(e) => setOptimizeOnBudget(e.target.checked)}
                            leftLabel="Days"
                            rightLabel="Budget"
                            disabled={fetchingConstraints}
                        />
                    </div>

                    <div className="columns">
                        <div className="column">
                            <FilmaizeText type="number"
                                label="Script Pages Per Day"
                                value={scriptPagesPerDay}
                                onChange={(value) => setScriptPagesPerDay(value ? Number(value) : 3)}
                                min={1}
                                inputStyle={{ maxWidth: '100px' }}
                                disabled={fetchingConstraints} />
                        </div>
                        <div className="column">
                            <FilmaizeText type="number"
                                label="Target shooting days"
                                value={targetShootingDays}
                                placeholder="Days"
                                onChange={(value) => setTargetShootingDays(value ? Number(value) : undefined)}
                                min={1}
                                inputStyle={{ maxWidth: '100px' }}
                                disabled={fetchingConstraints} />
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">
                            <Time
                                label={"Day length"}
                                value={dayLength}
                                onChange={setDayLength}
                                minuteGap={15}
                                canAddDay={false}
                                disabled={fetchingConstraints}
                            />
                        </div>
                        <div className="column">
                            <FilmaizeText type="number"
                                label="Percentage over day length allowed"
                                value={percentageOverDayLengthAllowed}
                                onChange={(value) => setPercentageOverDayLengthAllowed(value ? Number(value) : 20)}
                                min={1}
                                inputStyle={{ maxWidth: '100px' }}
                                disabled={fetchingConstraints} />
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">
                            <Time
                                label={"Earliest Call Time"}
                                value={earliestCallTime}
                                onChange={setEarliestCallTime}
                                minuteGap={15}
                                canAddDay={false}
                                disabled={fetchingConstraints}
                            />
                        </div>
                        <div className="column">
                            <Time
                                label={"Sunset Time"}
                                infoText="Only used if location or date not available"
                                value={sunsetTime}
                                onChange={setSunsetTime}
                                minuteGap={15}
                                canAddDay={false}
                                disabled={fetchingConstraints}
                            />
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">
                            <FilmanizeCheckbox
                                label="Allow multiple locations per day"
                                checked={allowMultipleLocationsPerDay}
                                onChange={setAllowMultipleLocationsPerDay}
                                disabled={fetchingConstraints}
                            />
                            {allowMultipleLocationsPerDay &&
                                <Time
                                    className="mt-1"
                                    label={"Maximum travel time between locations"}
                                    value={maximumTravelTimeBetweenLocations}
                                    onChange={setMaximumTravelTimeBetweenLocations}
                                    minuteGap={5}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />}
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">
                            <Time
                                label={"Lunch duration"}
                                value={lunchDuration}
                                onChange={setLunchDuration}
                                minuteGap={15}
                                canAddDay={false}
                                disabled={fetchingConstraints}
                            />
                        </div>
                        <div className="column">
                            <Time
                                label={"Dinner duration"}
                                value={dinnerDuration}
                                onChange={setDinnerDuration}
                                minuteGap={15}
                                canAddDay={false}
                                disabled={fetchingConstraints}
                            />
                        </div>
                    </div>

                    <div className="box mt-2">
                        <div className="columns">
                            <div className="column">
                                <Time
                                    label={"Lunch window from"}
                                    value={lunchWindowFrom}
                                    onChange={setLunchWindowFrom}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                            <div className="column">
                                <Time
                                    label={"Lunch window to"}
                                    value={lunchWindowTo}
                                    onChange={setLunchWindowTo}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="box mt-2">
                        <div className="columns">
                            <div className="column">
                                <Time
                                    label={"Dinner window from"}
                                    value={dinnerWindowFrom}
                                    onChange={setDinnerWindowFrom}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                            <div className="column">
                                <Time
                                    label={"Dinner window to"}
                                    value={dinnerWindowTo}
                                    onChange={setDinnerWindowTo}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="box mt-2">
                        <label className="label">Meals Per Day Thresholds</label>
                        <div className="columns">
                            <div className="column">
                                <Time
                                    label={"No meals if less than"}
                                    value={mealThreshold1}
                                    onChange={setMealThreshold1}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                            <div className="column">
                                <Time
                                    label={"One meal if less than"}
                                    value={mealThreshold2}
                                    onChange={setMealThreshold2}
                                    minuteGap={15}
                                    canAddDay={false}
                                    disabled={fetchingConstraints}
                                />
                            </div>
                        </div>
                        <p className="help">2 meals will be provided for {mealThreshold2.hours}+ hours</p>
                    </div>

                    <Progress progress={creatingScheduleProgress} />

                    <div className="buttons mt-2">
                        <button className={`button is-primary ${creatingScheduleLoading ? "is-loading" : ""}`} disabled={creatingScheduleLoading || fetchingConstraints || (creatingScheduleProgress && creatingScheduleProgress.progress < 100)}
                            onClick={onCreateSchedule}>Create Schedule</button>
                    </div>

                </>}
            </div>
        </Collapsible>
    </div>
}

export default SmarterDays;